import { Button, ButtonColor, ButtonVariant } from '@components/material-ui';
import styled from '@emotion/styled';
import { breakpoints, mq } from '@styles/breakpoints';
import React from 'react';
import { useMedia } from 'react-use';
import { ZIndex } from '@styles/z-index';
import { BaseBox } from '@components/common/box/base/BaseBox';
import { boxShadowMixins } from '@styles/mixins';
import { SkeletonElement, SkeletonVariant } from '@components/common/skeleton-loading';
import { Icon } from '@components/common/generic-icon';
import { Icons } from '@model/common/icons';
import { ThemeProps } from '@theme/base';
import { WhatsIncluded } from '@components/basket/v2/basket-panel/WhatsIncluded';

export interface Labels {
  desktop: string;
  mobile: string;
}

export interface BasketPanelProps {
  render: any;
  labels: Labels;
  onClick: () => void;
  children?: any;
  disabled?: boolean;
  className?: string;
  buttonColor?: ButtonColor;
  buttonVariant?: ButtonVariant;
  buttonId?: string;
  showCta?: boolean;
  isLoading?: boolean;
  isGhosting?: boolean;
  inline?: boolean;
  isTour?: boolean;
}

const ContainerColor = styled(BaseBox)(({ theme }: ThemeProps) => ({
  background: theme.custom.colors.white,
  borderWidth: 1,
  [mq.medium]: {
    borderRadius: 3
  }
}));

interface ContainerProps extends ThemeProps {
  inline?: boolean;
}

const Container: any = styled.div(({ inline, theme }: ContainerProps) => ({
  display: 'flex',
  justifyContent: 'space-between',
  position: inline ? 'static' : 'fixed',
  width: '100%',
  bottom: 0,
  left: 0,
  zIndex: ZIndex.PRODUCT_PRICE,
  alignItems: 'center',
  marginTop: inline ? theme.custom.spacing.medium : 0,
  height: 'auto',
  [mq.medium]: {
    margin: 0,
    flexDirection: 'column',
    position: 'relative',
    alignItems: 'flex-start'
  }
}));

const PriceContainer: any = styled(ContainerColor)(({ theme }: ThemeProps) => ({
  borderRadius: `${theme.custom.spacing.small}px ${theme.custom.spacing.small}px 0 0`,
  padding: theme.custom.spacing.medium,
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  bottom: 0,
  left: 0,
  alignItems: 'center',
  position: 'relative',
  [mq.medium]: {
    borderRadius: 10,
    width: '100%',
    padding: theme.custom.spacing.large,
    margin: 0,
    flexDirection: 'column',
    marginBottom: theme.custom.spacing.large,
    alignItems: 'flex-start',
    boxShadow: boxShadowMixins(theme.custom.colors.group10.base).default
  }
}));

interface InlineProps extends ThemeProps {
  inline?: boolean;
}

export const ActionButton: any = styled(Button)(({ theme, inline }: InlineProps) => ({
  marginTop: 0,
  width: inline ? '100%' : '50%',
  [mq.medium]: {
    marginTop: theme.custom.spacing.large,
    width: '100%'
  }
}));

const BodyContainer: any = styled.div(({ theme, inline }: InlineProps) => ({
  display: inline ? 'block' : 'flex',
  height: '100%',
  width: '100%',
  transition: `opacity ${theme.custom.transitions.default}`,
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  [mq.medium]: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  }
}));

const ArrowIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.6rem',
  color: theme.custom.colors.white
}));

export const BasketPanel = ({
  render,
  labels,
  onClick,
  children,
  disabled,
  className,
  buttonColor,
  buttonVariant,
  showCta = true,
  isLoading,
  isGhosting,
  buttonId,
  inline,
  isTour
}: BasketPanelProps) => {
  const isMobile = useMedia(`(max-width: ${breakpoints.medium}px)`);
  const label: string = isMobile ? labels.mobile : labels.desktop;

  const renderButton = () => {
    if (!showCta) return null;
    return isGhosting ? (
      <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} />
    ) : (
      <ActionButton
        buttonId={buttonId}
        label={label}
        onClick={onClick}
        disabled={disabled || !!isLoading}
        variant={buttonVariant}
        color={buttonColor}
        inline={inline}
        endIcon={inline ? <ArrowIcon name={Icons.ARROW_RIGHT} /> : null}
      />
    );
  };

  return (
    <Container className={className} inline={inline}>
      <PriceContainer>
        <BodyContainer inline={inline}>
          {render}
          {renderButton()}
        </BodyContainer>
        {!isTour && <WhatsIncluded />}
      </PriceContainer>
      {children}
    </Container>
  );
};

BasketPanel.defaultProps = {
  buttonColor: ButtonColor.SECONDARY,
  buttonVariant: ButtonVariant.CONTAINED
};
